/* eslint-disable react/prop-types */
'use client';

import { useMemo } from 'react';
import {
  PackageCheck,
  FileText,
  CalendarDays,
  BadgePercent,
  Landmark,
  ShieldCheck,
  Sparkles,
  ShoppingBag,
  Truck,
  Clock,
} from 'lucide-react';

// ====================================================================================
// Data Helper Functions
// ====================================================================================

/**
 * Normalizes a list of attribute objects into a consistent key-value pair format.
 * It also attempts to parse JSON strings and convert numeric strings to numbers.
 */
function normalize(list) {
  const pairs = [];
  (Array.isArray(list) ? list : []).forEach((it, i) => {
    const key = (it?.attributeName || it?.name || `Attribute ${i + 1}`).toString();
    let val = it?.attributeValue ?? it?.value ?? '';
    try {
      if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
        val = JSON.parse(val);
      } else if (typeof val === 'string' && !isNaN(val) && val.trim() !== '') {
        val = Number(val);
      }
    } catch (err) {
      console.error(`Failed to parse attribute value: ${val}`, err);
    }
    pairs.push({ key, value: val });
  });
  return pairs;
}

/**
 * Checks if a value can be considered "truthy" in a business context.
 */
function isTruthy(v) {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const s = v.toLowerCase();
    return s === 'true' || s === 'yes' || s === 'enabled';
  }
  return !!v;
}

// Helper functions for business hours
function safeJsonParse(jsonString, fallback = {}) {
  if (typeof jsonString !== 'string' || !jsonString) return fallback;
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    console.error('Failed to parse JSON string:', jsonString, e);
    return fallback;
  }
}

function formatTime(timeStr) {
  if (!timeStr || !/^\d{2}:\d{2}$/.test(timeStr)) return '';
  const [hours, minutes] = timeStr.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const formattedHour = h % 12 === 0 ? 12 : h % 12;
  return `${formattedHour}:${minutes} ${ampm}`;
}

function addHour(timeStr) {
  if (!timeStr || !/^\d{2}:\d{2}$/.test(timeStr)) return '';
  const [hours, minutes] = timeStr.split(':');
  let h = parseInt(hours, 10);
  h = (h + 1) % 24;
  const newHours = h.toString().padStart(2, '0');
  return `${newHours}:${minutes}`;
}

// ====================================================================================
// Main Component: BusinessAttributes
// ====================================================================================

export default function BusinessAttributes({ attributeValues = [], attr = {} }) {
  // Business Hours calculation
  const schedule = useMemo(() => {
    const shopTiming = safeJsonParse(attr?.shopTiming);
    const offHours = safeJsonParse(attr?.offHours);
    const weeklyOffDays = (attr?.weeklyOffDay || '')
      .split(';')
      .filter(Boolean)
      .map((d) => d.toLowerCase());

    const breakStartStr = offHours.start;
    const breakEndStr =
      offHours.end && /^\d{2}:\d{2}$/.test(offHours.end) ? offHours.end : addHour(breakStartStr);

    const openTime = formatTime(shopTiming.start);
    const closeTime = formatTime(shopTiming.end);
    const breakStart = formatTime(breakStartStr);
    const breakEnd = formatTime(breakEndStr);

    let hoursInfo = {
      main: 'Not Available',
      break: null,
      isClosed: true,
    };

    if (openTime && closeTime) {
      hoursInfo = {
        main: `${openTime} - ${closeTime}`,
        break: breakStart ? `Break: ${breakStart} - ${breakEnd}` : null,
        isClosed: false,
      };
    }

    const daysOfWeek = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    const todayIndex = new Date().getDay();

    return daysOfWeek.map((day, index) => {
      const isToday = index === todayIndex;
      if (weeklyOffDays.includes(day.toLowerCase())) {
        return {
          day,
          isToday,
          status: 'Closed',
          breakInfo: null,
          isClosed: true,
        };
      }
      return {
        day,
        isToday,
        status: hoursInfo.main,
        breakInfo: hoursInfo.break,
        isClosed: hoursInfo.isClosed,
      };
    });
  }, [attr]);

  const { highlights, legalDetails } = useMemo(() => {
    const pairs = normalize(attributeValues);
    const attrMap = new Map(pairs.map((p) => [p.key, p.value]));

    const highlights = [];

    // 1. Process Highlights
    const deliveryCharges = attrMap.get('deliveryCharges');
    const minimumOrder = attrMap.get('minimumOrder');

    if (deliveryCharges > 0 && minimumOrder > 0) {
      highlights.push({
        icon: PackageCheck,
        title: `Free Delivery Over ₹${minimumOrder}`,
        description: `₹${deliveryCharges} fee for smaller orders.`,
      });
    } else if (deliveryCharges === 0 && minimumOrder > 0) {
      highlights.push({
        icon: PackageCheck,
        title: 'Free Delivery',
        description: `Minimum order: ₹${minimumOrder}.`,
      });
    } else if (deliveryCharges > 0) {
      highlights.push({
        icon: Truck,
        title: `Flat ₹${deliveryCharges} Delivery Fee`,
        description: 'Convenience delivered to you.',
      });
    }

    const establishedYear = attrMap.get('establishedYear');
    if (establishedYear) {
      const currentYear = new Date().getFullYear();
      const yearsInBusiness = currentYear - establishedYear;
      if (yearsInBusiness <= 0) {
        highlights.push({
          icon: Sparkles,
          title: 'Newly Established',
          description: `Serving you since ${establishedYear}.`,
        });
      } else {
        highlights.push({
          icon: CalendarDays,
          title: `${yearsInBusiness}+ Years of Experience`,
          description: `Established in ${establishedYear}.`,
        });
      }
    }

    const deliveryType = attrMap.get('productDeliveryType');
    if (deliveryType) {
      let title = 'Flexible Options';
      if (String(deliveryType).toLowerCase() === 'both') title = 'Home Delivery & Self Pickup';
      else title = deliveryType;
      highlights.push({ icon: ShoppingBag, title, description: 'Choose what suits you best.' });
    }

    const certifications = attrMap.get('certifications');
    if (certifications && typeof certifications === 'string') {
      const [certName] = certifications.split(';');
      if (certName) {
        highlights.push({
          icon: ShieldCheck,
          title: `${certName} Certified`,
          description: 'Quality and safety you can trust.',
        });
      }
    }

    // 2. Process Legal & Business Details
    const legalDetails = [];
    const legalKeys = {
      gstNo: { label: 'GST Number', icon: BadgePercent },
      panNo: { label: 'PAN Number', icon: FileText },
      udyamNo: { label: 'Udyam Registration', icon: FileText },
    };
    for (const key in legalKeys) {
      if (attrMap.has(key) && attrMap.get(key)) {
        legalDetails.push({ ...legalKeys[key], value: attrMap.get(key) });
      }
    }
    if (isTruthy(attrMap.get('isFranchise'))) {
      const franchiseName = attrMap.get('franchiseName');
      legalDetails.push({
        icon: Landmark,
        label: 'Franchise',
        value: franchiseName || 'Part of a trusted network',
      });
    }

    return { highlights, legalDetails };
  }, [attributeValues]);

  return (
    <div className="space-y-6 font-sans p-2 pt-4">
      {/* Business Hours Section */}
      <BusinessHoursSection schedule={schedule} />
      {/* Highlights Section */}
      {highlights.length > 0 && <HighlightsSection highlights={highlights} />}
      {/* Legal Details Section */}
      {legalDetails.length > 0 && (
        <InfoCard title="Business Details">
          {legalDetails.map((item) => (
            <InfoRow key={item.label} {...item} />
          ))}
        </InfoCard>
      )}
    </div>
  );
}

// ====================================================================================
// UI Sub-Components
// ====================================================================================

function BusinessHoursSection({ schedule }) {
  return (
    <div className="rounded-xl border bg-white p-4 shadow-sm sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="size-5 text-emerald-600" />
        <h3 className="text-lg font-semibold text-slate-800 tracking-tight">Business Hours</h3>
      </div>
      <ul className="space-y-3 md:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 md:gap-x-6 md:gap-y-3 md:space-y-0">
        {schedule.map(({ day, isToday, status, breakInfo, isClosed }) => (
          <li
            key={day}
            className={`flex items-start gap-3 rounded-lg p-2.5 text-sm ${
              isToday ? 'bg-emerald-50 ring-1 ring-emerald-200' : ''
            }`}
          >
            <span
              className={`w-20 flex-shrink-0 font-bold ${
                isToday ? 'text-emerald-700' : 'text-slate-700'
              }`}
            >
              {day}
            </span>
            <div>
              {isClosed ? (
                <span className="font-semibold text-red-500">{status}</span>
              ) : (
                <>
                  <span className="font-medium text-slate-700">{status}</span>
                  {breakInfo && (
                    <span className="mt-0.5 block text-xs text-amber-600">{breakInfo}</span>
                  )}
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function HighlightsSection({ highlights }) {
  return (
    <div>
      <h3 className="mb-4 text-xl font-bold text-slate-800 tracking-tight">Why Shop With Us?</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {highlights.map((item, index) => (
          <div
            key={index}
            className="rounded-xl border border-slate-200/80 bg-white p-4 text-center shadow-sm transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg"
          >
            <div className="mx-auto grid size-12 place-items-center rounded-full bg-emerald-100 text-emerald-600">
              <item.icon className="size-6" />
            </div>
            <p className="mt-3 text-sm font-semibold text-slate-800">{item.title}</p>
            <p className="mt-1 text-xs text-slate-500">{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function InfoCard({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm h-full">
      <h3 className="mb-4 text-lg font-semibold text-slate-800 tracking-tight">{title}</h3>
      <div className="flex flex-wrap items-center gap-6">{children}</div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex w-fit items-start gap-4">
      <div className="mt-1 grid size-8 flex-shrink-0 place-items-center rounded-full bg-slate-100 text-slate-600">
        <Icon className="size-5" />
      </div>
      <div>
        <div className="font-medium text-slate-800">{label}</div>
        {value && <div className="text-sm text-slate-500">{value}</div>}
      </div>
    </div>
  );
}
